import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Card, ModernCardHeader } from "../../../../components/cards";

import {
  Euro,
  ShoppingCart,
  Store,
  Link as LinkIcon,
  TrendingUp,
  Warehouse,
} from "lucide-react";

import {
  ProductScansPreview,
  ProductPriceVariations,
  ProductStock,
  ProductTurnover,
} from "./composants";
import DatePicker from "react-datepicker";
import { useLazyScansByProduct } from "../../../scans/hooks";
import { StockSalesGraph } from "../../../scans/components/graphs/stock-sales-graph";
import { StockRestockingGraph } from "../../../scans/components/graphs/stock-restocking-graph";
import { useProduct } from "../../hooks";
import { formatNumber } from "../../../../helpers/string";

type IProductParams = {
  id: string;
};

export const Product: React.FC = () => {
  const { id } = useParams<IProductParams>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/products");
    }
  }, []);

  let [startDate, setStartDate] = React.useState<Date | null>(null);
  let [endDate, setEndDate] = React.useState<Date | null>(null);
  let [groupBy, setGroupBy] = React.useState<string>("day");

  const { data: productData, loading: productLoading } = useProduct(+id!);
  const [search, { data, loading, error }] = useLazyScansByProduct();

  const isPriceOnly = productData?.product?.result?.priceOnly;

  React.useEffect(() => {
    search({ variables: { productId: +id! } });
  }, []);

  React.useEffect(() => {
    search({
      variables: {
        productId: +id!,
        where: {
          start: startDate?.toISOString(),
          end: endDate?.toISOString(),
        },
        groupBy,
      },
    });
  }, [startDate, endDate, groupBy]);

  if (productLoading) {
    return (
      <div className="main-container">
        <p>Chargement du produit...</p>
      </div>
    );
  }
  return (
    <>
      {/* Header with product info */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200 mb-2">
        <div className="main-container pt-4">
          <div className="flex items-center justify-between">
            <div className="">
              <h1 className="text-xl font-bold text-gray-900 mr-2">
                {productData?.product?.result?.name ?? "Chargement..."}
              </h1>
              <div className="flex gap-6 text-sm text-gray-600">
                <div
                  className="flex items-center gap-2 cursor-pointer hover:text-primary-600 transition-colors"
                  onClick={() =>
                    navigate(`/site/${productData?.product?.result?.siteId}`)
                  }
                >
                  <Store size={14} className="text-gray-500" />
                  <span>{productData?.product?.result?.site?.name}</span>
                </div>
                {productData?.product?.result?.url && (
                  <a
                    href={`https://www.google.com/search?q=${productData?.product?.result?.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:text-primary-600 transition-colors"
                  >
                    <LinkIcon size={14} className="text-gray-500" />
                    <span>Voir le produit</span>
                  </a>
                )}
              </div>
            </div>
            {productData?.product?.result?.imgUrl && (
              <img
                width={80}
                height={80}
                src={productData?.product?.result?.imgUrl}
                alt={productData?.product?.result?.name}
                className="rounded-lg object-cover border-2 border-gray-200"
              />
            )}
          </div>
        </div>
        {/* Filters sticky bar */}
        <div className="sticky z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="main-container pb-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Filtres:
              </span>
              <div className="flex gap-3 flex-1">
                <div className="flex-1">
                  <DatePicker
                    locale={"fr"}
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholderText="Date début"
                    className="input w-full text-sm"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="flex-1">
                  <DatePicker
                    locale={"fr"}
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholderText="Date fin"
                    className="input w-full text-sm"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="w-40">
                  <select
                    className="input w-full text-sm"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <option value="day">Par jour</option>
                    <option value="week">Par semaine</option>
                    <option value="month">Par mois</option>
                    <option value="year">Par année</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="main-container px-4 ">
        <div className="section">
          {/* Stats Cards */}
          <div className="w-full mb-2">
            <div
              className={`grid ${
                isPriceOnly
                  ? "grid-cols-1 max-w-sm"
                  : "grid-cols-2 xl:grid-cols-4"
              } gap-2`}
            >
              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Prix
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(productData?.product?.result?.lastPrice)} €
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Prix actuel
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Euro className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                  </div>
                </div>
              </Card>

              {!isPriceOnly && (
                <>
                  <Card
                    hover
                    padding="sm"
                    className="shadow-sm border border-gray-200"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Stock
                        </p>
                        <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                          {formatNumber(
                            productData?.product?.result?.lastQuantity
                          )}{" "}
                          pcs
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                          Quantité disponible
                        </p>
                      </div>
                      <div className="bg-amber-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                        <Warehouse className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                      </div>
                    </div>
                  </Card>

                  <Card
                    hover
                    padding="sm"
                    className="shadow-sm border border-gray-200"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Chiffre d'affaire
                        </p>
                        <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                          {formatNumber(
                            data?.scansByProduct?.results?.reduce(
                              (acc, scan) => acc + (scan.ca ?? 0),
                              0
                            )
                          )}{" "}
                          €
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                          Total des ventes
                        </p>
                      </div>
                      <div className="bg-primary-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                        <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                      </div>
                    </div>
                  </Card>

                  <Card
                    hover
                    padding="sm"
                    className="shadow-sm border border-gray-200"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Produits Vendus
                        </p>
                        <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                          {formatNumber(
                            data?.scansByProduct?.results?.reduce(
                              (acc, scan) => acc + Math.abs(scan.salesSum ?? 0),
                              0
                            )
                          )}{" "}
                          pcs
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                          Nombre total
                        </p>
                      </div>
                      <div className="bg-rose-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                        <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </div>
          </div>

          {/* Graph Cards */}
          {!isPriceOnly && (
            <>
              <div className="w-full md:w-1/2 pr-1">
                <Card
                  padding="md"
                  className="mb-3 shadow-sm border border-gray-200"
                >
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Chiffre d'affaire
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Évolution des ventes
                    </p>
                  </div>
                  <div className="mt-3">
                    <ProductTurnover
                      data={data?.scansByProduct?.results}
                      loading={loading}
                    />
                  </div>
                </Card>
              </div>

              <div className="w-full md:w-1/2 pl-1">
                <Card
                  padding="md"
                  className="mb-3 shadow-sm border border-gray-200"
                >
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Stock
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Évolution du stock
                    </p>
                  </div>
                  <div className="mt-3">
                    <ProductStock
                      data={data?.scansByProduct?.results}
                      loading={loading}
                    />
                  </div>
                </Card>
              </div>

              <div className="w-full md:w-1/2 pr-1">
                <Card
                  padding="md"
                  className="mb-3 shadow-sm border border-gray-200"
                >
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Ventes
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Produits vendus
                    </p>
                  </div>
                  <div className="mt-3">
                    <StockSalesGraph data={data?.scansByProduct?.results} />
                  </div>
                </Card>
              </div>

              <div className="w-full md:w-1/2 pl-1">
                <Card
                  padding="md"
                  className="mb-3 shadow-sm border border-gray-200"
                >
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Réassort
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Réapprovisionnement
                    </p>
                  </div>
                  <div className="mt-3">
                    <StockRestockingGraph
                      data={data?.scansByProduct?.results}
                    />
                  </div>
                </Card>
              </div>
            </>
          )}

          <div className={isPriceOnly ? "w-full" : "w-full md:w-1/2 pr-1"}>
            <Card
              padding="md"
              className="mb-3 shadow-sm border border-gray-200"
            >
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Variations de prix
                </h3>
                <p className="text-xs text-gray-500 mt-1">Évolution des prix</p>
              </div>
              <div className="mt-3">
                <ProductPriceVariations
                  data={data?.scansByProduct?.results}
                  loading={loading}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};
